import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Chip, Dialog, Icon } from '@clientbase/clientbase-library'
import { Box, Chip as MuiChip, Stack, Tooltip, Typography } from '@mui/material'
import { useRemoveBillingCoupon } from 'features/coupons/api/useRemoveBillingCoupon'
import CouponValueDisplay from 'features/coupons/components/CouponValueDisplay'
import moment from 'moment'

import { DetailCompanyCell } from 'components/DetailCompany'
import DetailSummary, {
  Celula,
  DescricaoRecolhivel,
  ListaChips,
  NaoConfigurado,
  PagadorFaixa
} from 'components/DetailSummary'
import { ChipTagData } from 'components/Tag'
import ChipTag from 'components/Tag'

import { formatCpfCnpj, formatMoney, formatPhone } from 'utils'
import { formatDate, stringToDate } from 'utils/FormatDate'
import { convertTagsStringToArray } from 'utils/tags'

import { theme } from 'styles/theme'

import PaymentTypeDetailsView from '../../../ModalRecurrenceDetail/components/PaymentTypeDetailsView'
import { ModalChargeDetailContext } from '../../context'
import { DiscountPolicyEnum } from '../../ModalChargeDetail.utils'
import CancelConfirmationModal from '../CancelConfirmationDialog/CancelConfirmationDialog'
import PayerActions from '../PayerActions/Actions'
import * as S from './BillingDetails.styles'
interface BillingDetailsParams {
  updateReload: (value: React.SetStateAction<boolean>) => void
  reload: boolean
  setReloadTable?: React.Dispatch<React.SetStateAction<boolean>>
}
const BillingDetails = ({
  updateReload,
  reload,
  setReloadTable
}: BillingDetailsParams) => {
  const { invoice, updateModal, paymentType, realtimeTags } = useContext(
    ModalChargeDetailContext
  )

  // Use real-time tags if available, fallback to invoice tags
  const billingTags: ChipTagData[] =
    realtimeTags.length > 0
      ? realtimeTags.map((tag) => ({
          id: tag.id,
          name: tag.name,
          color: tag.color || '#000000'
        }))
      : convertTagsStringToArray(
          invoice?.tags,
          [] // We don't need available tags for display only
        )

  const [isOpenCancellationDialog, setOpenCancellationDialog] =
    useState<boolean>(false)
  const handleCloseCancellationDialog = () => {
    setOpenCancellationDialog(!isOpenCancellationDialog)
  }

  // Coupon removal
  const { removeBillingCoupon, loading: removingCoupon } =
    useRemoveBillingCoupon()
  const [isOpenRemoveCouponDialog, setIsOpenRemoveCouponDialog] =
    useState(false)

  const isBillingOpen = ['pending', 'created_payment', 'open_payment'].includes(
    invoice?.status || ''
  )

  const handleRemoveCoupon = async () => {
    try {
      const result = await removeBillingCoupon(invoice.uuid)
      setIsOpenRemoveCouponDialog(false)

      if (result.quota_returned) {
        toast.success(
          `Cupom removido. +1 uso devolvido à quota do cupom ${invoice.couponUsage?.couponCode}`
        )
      } else {
        toast.success(
          'Cupom removido. A quota não foi devolvida pois já houve pagamento nesta contratação'
        )
      }

      updateReload(!reload)
      setReloadTable && setReloadTable((s) => !s)
    } catch (error) {
      toast.error(String(error))
    }
  }

  const handleOpenDetailRecurrence = (uuid: string) => {
    updateModal((prevState) => ({
      ...prevState,
      uuidRecurrenceDetail: uuid,
      isOpenModalRecurrenceDetail: true
    }))
  }

  const reasonTypeMapper = {
    wrong_billing: 'Cobrança indevida',
    service_cancellation: 'Cliente cancelou a prestação de serviços',
    issue_error: 'Erro na emissão',
    non_payment: 'Cliente inadimplente',
    other: 'Outros motivos'
  }

  const meiosDePagamentoVisiveis = paymentType.filter(Boolean)

  // A célula vira larga por contagem OU por rótulo comprido: "RECEBIMENTO
  // MANUAL" sozinho já não cabe na coluna de 140px e era truncado com "...".
  const meioDePagamentoComprido = meiosDePagamentoVisiveis.some(
    (item: any) => String(item).length > 12
  )

  // Juros e multa compartilham a mesma chave: `no_interest` desliga os dois, e
  // cada um ainda pode vir zerado individualmente.
  const semJurosEMulta = invoice?.interestPolicy === 'no_interest'
  const multa = Number(invoice?.interestFine).toFixed(2).replace('.', ',')
  const juros = Number(invoice?.interestFee).toFixed(2).replace('.', ',')

  // Guardado numa variável para o TypeScript estreitar o tipo dentro do JSX:
  // `invoice.couponUsage` é opcional e o narrowing não sobrevive ao ternário.
  const cupomAtivo =
    invoice?.couponUsage && !invoice.couponUsage.removedAt
      ? invoice.couponUsage
      : undefined

  return (
    <S.Wrapper>
      <S.ChargeSession>
        <DetailSummary
          pagador={
            <PagadorFaixa
              nome={invoice?.customer?.name || '-'}
              documento={formatCpfCnpj(invoice?.customer?.document || '')}
              email={invoice?.customer?.email || ''}
              telefone={formatPhone(invoice?.customer?.phone || '')}
              href={
                invoice?.customerUuid
                  ? `/clientes/${invoice.customerUuid}`
                  : undefined
              }
              acoes={<PayerActions />}
            />
          }
          valor={
            cupomAtivo ? (
              <CouponValueDisplay
                originalAmount={
                  Number(invoice.amountBilled) +
                  Number(cupomAtivo.appliedDiscountAmount)
                }
                finalAmount={Number(invoice.amountBilled)}
                couponUsage={cupomAtivo}
                canRemove={isBillingOpen}
                onRemoveClick={() => setIsOpenRemoveCouponDialog(true)}
              />
            ) : (
              formatMoney(invoice?.amountBilled)
            )
          }
          cadencia={`Vence em ${formatDate(invoice?.dueDate as string)}`}
          destaques={
            <>
              {Number(invoice?.amountBilled) === 0 && (
                <MuiChip
                  label="Bonificado"
                  size="small"
                  sx={{
                    fontSize: '11px',
                    height: '22px',
                    background: theme.palette.primary[500],
                    color: theme.palette.primary[200]
                  }}
                />
              )}
              {/* Rótulo neutro de propósito: `amount_updated` tanto sobe
                  (juros e multa após o vencimento) quanto desce (desconto
                  vigente), então nomear a causa aqui erraria metade dos casos. */}
              {/* `> 0` e não só truthy: cobrança paga volta com
                  `amount_updated: 0`, que passava no truthy da comparação com
                  o valor cobrado e virava um "Valor atualizado R$ 0,00". */}
              {Number(invoice?.amountUpdated) > 0 &&
                Number(invoice.amountUpdated) !==
                  Number(invoice.amountBilled) && (
                  <MuiChip
                    label={`Valor atualizado ${formatMoney(
                      invoice.amountUpdated
                    )}`}
                    size="small"
                    sx={{ fontSize: '11px', height: '22px' }}
                  />
                )}
              {invoice?.recurrenceCycle > 0 && (
                <Tooltip title="Abrir a recorrência que gerou esta cobrança">
                  {/* span porque o Chip não encaminha os handlers do tooltip.
                      Sem `onDelete`: o ícone dele engole o hover e o tooltip
                      nunca aparecia — o `open` vai como `icon` mesmo. */}
                  <span style={{ display: 'inline-flex' }}>
                    <MuiChip
                      label={`Ciclo ${invoice.recurrenceCycle}`}
                      size="small"
                      clickable
                      onClick={() =>
                        handleOpenDetailRecurrence(invoice?.recurrenceUuid)
                      }
                      icon={
                        <Icon
                          icon="open"
                          sx={{ fontSize: '15px' }}
                          colorSX={theme.palette.neutral[400]}
                        />
                      }
                      sx={{ fontSize: '11px', height: '22px' }}
                    />
                  </span>
                </Tooltip>
              )}
            </>
          }
        >
          {/* SC-10999: de qual empresa é a cobrança, na visão multi-empresas */}
          <DetailCompanyCell recordUuid={invoice?.uuid} />

          {/* Datas de APOIO: recuam para o vencimento (no cartão do valor)
              ficar como a data que decide a cobrança. */}
          <Celula label="Data de expiração" secundaria>
            {formatDate(invoice?.expirationField as string)}
          </Celula>

          {(invoice?.issue_date || invoice?.issue_days) && (
            <Celula
              secundaria
              label={
                invoice?.recurrenceCycle > 0
                  ? invoice?.issue_billing_type === 'specific_day'
                    ? 'Dia de emissão'
                    : 'Emitir antes do venc.'
                  : 'Data de emissão'
              }
            >
              {invoice?.recurrenceCycle > 0
                ? invoice?.issue_billing_type === 'specific_day'
                  ? `Dia ${invoice?.issue_days}`
                  : `${invoice?.issue_days} dias`
                : formatDate(invoice?.issue_date as string)}
            </Celula>
          )}

          {invoice?.hasBonus && (
            <Celula label="Bonificação">
              {`${
                invoice.bonusSettings?.type === 'value'
                  ? formatMoney(invoice.bonusSettings.amount)
                  : Number(invoice.bonusSettings?.amount)
                      .toFixed(2)
                      .replace('.', ',')
              }${invoice.bonusSettings?.type === 'percentage' ? '%' : ''}`}
            </Celula>
          )}

          {invoice?.bonusCarryover && (
            // Saldo de bonificação de ciclos anteriores (SC-11262), separado do
            // bônus do próprio ciclo. Previsto até a emissão, aplicado depois.
            <Celula label="Saldo de bonificação">
              {`- ${formatMoney(invoice.bonusCarryover.amount)} (${
                invoice.bonusCarryover.status === 'applied'
                  ? 'aplicado'
                  : 'previsto'
              })`}
            </Celula>
          )}

          {DiscountPolicyEnum[invoice?.discountPolicy] === 'Sim' &&
          invoice?.discounts.length > 0 ? (
            invoice?.discounts.map((discount, index) => (
              <Celula
                key={index}
                label={
                  invoice?.discounts.length > 1
                    ? `Desconto ${index + 1}`
                    : 'Desconto'
                }
              >
                {`${
                  discount.policy == 'percentage'
                    ? `${Number(discount.amount).toFixed(2).replace('.', ',')}%`
                    : formatMoney(Number(discount.amount))
                } até ${moment(stringToDate(invoice?.dueDate as string))
                  .subtract(discount.days, 'days')
                  .format('DD/MM/YYYY')}`}
              </Celula>
            ))
          ) : (
            <Celula label="Desconto">
              <NaoConfigurado />
            </Celula>
          )}

          <Celula label="Multa">
            {semJurosEMulta || multa === '0,00' ? (
              <NaoConfigurado />
            ) : (
              `${multa}%`
            )}
          </Celula>

          <Celula label="Juros">
            {semJurosEMulta || juros === '0,00' ? (
              <NaoConfigurado />
            ) : (
              `${juros}% ao mês`
            )}
          </Celula>

          {/* Largura inteira só quando há muitos meios: com cinco chips eles
              ocupavam três linhas ao dividir espaço com a célula vizinha, mas
              com um ou dois a linha cheia desperdiça o espaço ao lado. */}
          <Celula
            label="Meios de pagamento"
            larga={
              meiosDePagamentoVisiveis.length > 2 || meioDePagamentoComprido
            }
          >
            <ListaChips>
              {paymentType.map(
                (item: any) =>
                  item && (
                    <Chip
                      variant="payment"
                      sx={(theme: any) => ({
                        background: theme.palette.neutral[400]
                      })}
                      key={String(item)}
                      label={String(item).toUpperCase()}
                    />
                  )
              )}
            </ListaChips>
          </Celula>

          {billingTags.length > 0 && (
            <Celula label="Tags" larga>
              <ListaChips>
                {billingTags.map((tag) => (
                  <ChipTag
                    key={tag.id}
                    tag={tag}
                    // No onDelete prop - this is display only
                  />
                ))}
              </ListaChips>
            </Celula>
          )}

          {invoice?.description && (
            <Celula label="Descrição" larga>
              <DescricaoRecolhivel texto={invoice.description} />
            </Celula>
          )}

          {invoice?.paymentTypeDetails && (
            <PaymentTypeDetailsView
              paymentTypeDetails={invoice.paymentTypeDetails}
            />
          )}
        </DetailSummary>

        {invoice?.cancellationReason?.reasonType && (
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Stack direction="row">
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                Motivo do cancelamento (
              </Typography>
              <Typography
                onClick={handleCloseCancellationDialog}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                variant="smLight"
                color={theme.palette.neutral[400]}
              >
                editar
              </Typography>
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                )
              </Typography>
            </Stack>
            <S.Separator sx={{ marginTop: '4px' }} />
            <Typography
              variant="smBold"
              display="flex"
              color={theme.palette.error['300']}
              alignItems="center"
            >
              {reasonTypeMapper[invoice.cancellationReason.reasonType]}
            </Typography>
          </Box>
        )}
        {invoice?.cancellationReason?.notes && (
          <Stack gap={1} margin="10px 0px 10px 0px" mt="16px">
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Observações do cancelamento
            </Typography>

            <Typography
              color={theme.palette.neutral[400]}
              sx={{ whiteSpace: 'pre-line' }}
            >
              {invoice?.cancellationReason?.notes}
            </Typography>
          </Stack>
        )}
      </S.ChargeSession>

      {updateReload && (
        <CancelConfirmationModal
          cancellationData={invoice.cancellationReason}
          cancelableNfses={invoice.cancelableNfses}
          isOpen={isOpenCancellationDialog}
          onClose={handleCloseCancellationDialog}
          billingUuid={invoice.uuid}
          onSuccess={() => {
            updateReload(!reload)
            setReloadTable && setReloadTable((s) => !s)
          }}
          type="charge"
        />
      )}

      <Dialog
        icon="info"
        title="Remover cupom da cobrança?"
        description="O valor da cobrança será recalculado sem o desconto do cupom"
        setIsOpenDialog={() => setIsOpenRemoveCouponDialog(false)}
        isOpenDialog={isOpenRemoveCouponDialog}
        cancelButton
        fullWidth
        maxWidth="xs"
      >
        <Button
          fullWidth
          onClick={handleRemoveCoupon}
          disabled={removingCoupon}
          loading={removingCoupon}
        >
          Remover
        </Button>
      </Dialog>
    </S.Wrapper>
  )
}

export default BillingDetails
